"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useParams } from "next/navigation";
import type { Conversation } from "@casey/db-types";
import {
  useInviteFlow,
  useVoiceChatApiClient,
} from "@/contexts/invite-flow-context";

// ============================================================================
// Types
// ============================================================================

export type BackendFlavor = "legacy" | "flow" | "elevenlabs" | "livekit";

/**
 * Conversation data for the current active conversation.
 * This is conversation-specific data, separate from connection/org info.
 */
export interface ConversationData {
  /** Conversation ID */
  id: string;
  /** Connection code this conversation belongs to */
  connectionCode: string | null;
  /** Conversation status */
  status: Conversation["status"] | null;
  /** Full conversation context (parsed JSON) */
  context: Record<string, unknown> | null;
  /** Session properties (parsed JSON) - UTM params, browser context, etc. */
  sessionProperties: Record<string, unknown> | null;
  /** Participant user ID (ext_*) */
  participantUserId: string | null;
  /** Organization ID */
  organizationId: string | null;
  /** Backend flavor */
  flavor: BackendFlavor | null;
  /** Transport credentials for Daily (Pipecat) */
  dailyRoom?: string;
  dailyToken?: string;
  /** Transport credentials for ElevenLabs */
  elToken?: string;
}

interface ConversationContextType {
  /** Current conversation data (null if not on a conversation page or not loaded) */
  conversation: ConversationData | null;
  /** Raw conversation data from API (for compatibility) */
  rawConversation: Partial<Conversation> | null;
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Update transport credentials */
  setTransportCredentials: (credentials: {
    dailyRoom?: string;
    dailyToken?: string;
    elToken?: string;
  }) => void;
  /** Update conversation status */
  setStatus: (status: Conversation["status"]) => void;
  /** Refetch conversation data from API */
  refetch: () => Promise<void>;
  /** Clear conversation data */
  clear: () => void;
}

const ConversationContext = createContext<ConversationContextType | undefined>(
  undefined,
);

// ============================================================================
// Provider
// ============================================================================

interface ConversationProviderProps {
  children: ReactNode;
}

/**
 * Provides conversation data to the component tree.
 *
 * Automatically fetches conversation data when on a `/live/[connectionCode]/[conversationId]`
 * route (or legacy `/live-interview/[conversationId]` route).
 *
 * Can also be populated from invite flow data for instant loading.
 */
export function ConversationProvider({ children }: ConversationProviderProps) {
  const params = useParams();
  // Support both new route (/live/[connectionCode]/[conversationId]) and legacy (/live-interview/[conversationId])
  const conversationId = (params?.conversationId || params?.conversationId) as
    | string
    | undefined;
  const { inviteFlowData } = useInviteFlow();
  const apiClient = useVoiceChatApiClient();

  const [conversation, setConversation] = useState<ConversationData | null>(
    null,
  );
  const [rawConversation, setRawConversation] =
    useState<Partial<Conversation> | null>(null);
  const [loading, setLoading] = useState(
    !!conversationId && conversationId !== "new",
  );
  const [error, setError] = useState<string | null>(null);
  const [initializedFromInvite, setInitializedFromInvite] = useState(false);

  // Track last fetched conversationId to prevent duplicate fetches
  const lastFetchedSessionIdRef = useRef<string | null>(null);
  const prevSessionIdRef = useRef<string | undefined>(conversationId);

  // Reset state when conversationId changes
  useEffect(() => {
    if (prevSessionIdRef.current !== conversationId && conversationId) {
      prevSessionIdRef.current = conversationId;
      if (conversation && conversation.id !== conversationId) {
        setInitializedFromInvite(false);
        lastFetchedSessionIdRef.current = null;
      }
    }
  }, [conversationId, conversation]);

  // Fetch conversation from API
  const fetchConversation = useCallback(async () => {
    if (!conversationId || conversationId === "new") {
      setLoading(false);
      return;
    }

    if (
      lastFetchedSessionIdRef.current === conversationId ||
      initializedFromInvite
    ) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // API client handles token refresh automatically via callback
      const data = await apiClient.getConversation(conversationId);

      // Parse context if string
      let parsedContext: Record<string, unknown> | null = null;
      try {
        parsedContext =
          typeof data.context === "string"
            ? JSON.parse(data.context)
            : (data.context as Record<string, unknown>) || null;
      } catch {
        console.error("Failed to parse conversation context");
      }

      // Parse sessionProperties if string
      let parsedSessionProperties: Record<string, unknown> | null = null;
      try {
        parsedSessionProperties =
          typeof data.sessionProperties === "string"
            ? JSON.parse(data.sessionProperties)
            : (data.sessionProperties as Record<string, unknown>) || null;
      } catch {
        console.error("Failed to parse session properties");
      }

      const conversationData: ConversationData = {
        id: data.id,
        connectionCode: data.connectionCode,
        status: data.status as Conversation["status"],
        context: parsedContext,
        sessionProperties: parsedSessionProperties,
        participantUserId: data.participantUserId || null,
        organizationId: data.organizationId || null,
        flavor: (data.flavor as BackendFlavor) || null,
      };

      setConversation(conversationData);
      setRawConversation({
        id: data.id,
        connectionCode: data.connectionCode,
        participantUserId: data.participantUserId,
        status: data.status as Conversation["status"],
        context: parsedContext,
        sessionProperties: parsedSessionProperties,
        organizationId: data.organizationId,
        createdAt: data.createdAt ? new Date(data.createdAt) : undefined,
      });
      lastFetchedSessionIdRef.current = conversationId;
      setInitializedFromInvite(false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "Unknown error";
      console.error("Error fetching conversation:", errorMessage);

      if (
        errorMessage.includes("not found") ||
        errorMessage.includes("access denied")
      ) {
        console.warn(
          "[ConversationProvider] Access denied - user may need to go through invite flow",
        );
      }

      setError(errorMessage);
      setConversation(null);
      setRawConversation(null);
    } finally {
      setLoading(false);
    }
  }, [
    conversationId,
    initializedFromInvite,
    inviteFlowData?.ephemeralToken,
    apiClient,
  ]);

  // Initialize from invite flow data
  useEffect(() => {
    if (inviteFlowData && conversationId && !initializedFromInvite) {
      const inviteSessionId = inviteFlowData.sessionData?.id;
      if (inviteSessionId === conversationId || !conversation) {
        setConversation({
          id: inviteSessionId || conversationId,
          connectionCode: inviteFlowData.connectionCode,
          status:
            (inviteFlowData.sessionData?.status as Conversation["status"]) ||
            "pending",
          context: inviteFlowData.sessionContext || null,
          sessionProperties:
            (inviteFlowData.sessionData?.sessionProperties as Record<
              string,
              unknown
            >) || null,
          participantUserId: null,
          organizationId: null,
          flavor: inviteFlowData.flavor || null,
          dailyRoom: inviteFlowData.dailyRoom,
          dailyToken: inviteFlowData.dailyToken,
          elToken: inviteFlowData.elToken,
        });
        setRawConversation({
          id: inviteSessionId || conversationId,
          connectionCode: inviteFlowData.connectionCode || undefined,
          status:
            (inviteFlowData.sessionData?.status as Conversation["status"]) ||
            "pending",
          context: inviteFlowData.sessionContext,
          sessionProperties: inviteFlowData.sessionData
            ?.sessionProperties as Record<string, unknown>,
        });
        setInitializedFromInvite(true);
        setLoading(false);
      }
    }
  }, [inviteFlowData, conversationId, initializedFromInvite, conversation]);

  // Fetch when conversationId changes (if not from invite flow)
  useEffect(() => {
    const inviteSessionId = inviteFlowData?.sessionData?.id;
    const hasInviteDataForSession = inviteSessionId === conversationId;

    if (
      conversationId &&
      conversationId !== "new" &&
      !initializedFromInvite &&
      !hasInviteDataForSession
    ) {
      fetchConversation();
    } else if (!conversationId || conversationId === "new") {
      if (!initializedFromInvite) {
        setConversation(null);
        setRawConversation(null);
      }
    }
  }, [
    conversationId,
    fetchConversation,
    initializedFromInvite,
    inviteFlowData?.sessionData?.id,
  ]);

  // Update transport credentials
  const setTransportCredentials = useCallback(
    (credentials: {
      dailyRoom?: string;
      dailyToken?: string;
      elToken?: string;
    }) => {
      setConversation((prev) =>
        prev
          ? {
              ...prev,
              dailyRoom: credentials.dailyRoom ?? prev.dailyRoom,
              dailyToken: credentials.dailyToken ?? prev.dailyToken,
              elToken: credentials.elToken ?? prev.elToken,
            }
          : null,
      );
    },
    [],
  );

  // Update status
  const setStatus = useCallback((status: Conversation["status"]) => {
    setConversation((prev) => (prev ? { ...prev, status } : null));
    setRawConversation((prev) => (prev ? { ...prev, status } : null));
  }, []);

  // Clear conversation
  const clear = useCallback(() => {
    setConversation(null);
    setRawConversation(null);
    setInitializedFromInvite(false);
    lastFetchedSessionIdRef.current = null;
    setError(null);
  }, []);

  return (
    <ConversationContext.Provider
      value={{
        conversation,
        rawConversation,
        loading,
        error,
        setTransportCredentials,
        setStatus,
        refetch: fetchConversation,
        clear,
      }}
    >
      {children}
    </ConversationContext.Provider>
  );
}

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook to access conversation data.
 */
export function useConversationContext() {
  const context = useContext(ConversationContext);
  if (context === undefined) {
    throw new Error("useConversation must be used within ConversationProvider");
  }
  return context;
}

/**
 * Alias for backward compatibility during migration.
 * @deprecated Use useConversationContext instead
 */
export function useSessionMetadata() {
  const ctx = useConversationContext();
  // Map to old interface for compatibility
  return {
    session: ctx.conversation
      ? {
          id: ctx.conversation.id,
          connectionCode: ctx.conversation.connectionCode,
          organizationName: null, // Now in invite-flow-context
          organizationAvatarUrl: null, // Now in invite-flow-context
          status: ctx.conversation.status,
          context: ctx.conversation.context,
          sessionProperties: ctx.conversation.sessionProperties,
          participantUserId: ctx.conversation.participantUserId,
          organizationId: ctx.conversation.organizationId,
          flavor: ctx.conversation.flavor,
        }
      : null,
    rawSession: ctx.rawConversation,
    loading: ctx.loading,
    error: ctx.error,
    refetch: ctx.refetch,
  };
}
